import React, { useEffect, useState, useMemo } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Space,
  Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useCampaignRequestorContext } from '../../../core/components/campaignRequestor/useCampaignRequestorContext';
import moment from 'moment-timezone';
import { usePermify } from '@permify/react-role';
import { isPercentageModel } from '../../../core/utils/pricing/cpmHelper';
import { useTimezone } from '../../../core/hooks/useTimezone';
import {
  toDatePickerDisplay,
  datePickerToStartOfDayUtc,
  datePickerToEndOfDayUtc,
  getDefaultStartDate,
  getDefaultEndDate,
} from './utils/dateNormalization';
import { clampCampaignsToEndDate } from './utils/cascadeDates';
import { applyRenewalToCampaigns } from './utils/renewalFlights';

const { Text } = Typography;

// Wrapper that converts Form.Item's injected UTC value to user timezone for display
const TimezoneDatePicker = ({ value, onChange, timezone, ...props }) => {
  const displayValue = toDatePickerDisplay(value, timezone);
  return <DatePicker {...props} value={displayValue} onChange={onChange} />;
};

const CampaignRequestorBudget = ({ formValues, derivedState, helpers }) => {
  const { currentUserOrg, currentCampaignOrder, selectedAdvertiser } =
    useCampaignRequestorContext();
  const { isAuthorized } = usePermify();
  const { timezone } = useTimezone();
  const [canEdit, setCanEdit] = useState(false);
  const [budgetInputValue, setBudgetInputValue] = useState(null);

  // Extract reactive values
  const { advertiserId, startDate, endDate, budget, cpm } = formValues;

  // Check if pricing model is Percentage
  // For existing campaigns, use the saved pricingModel; for new campaigns, use advertiser's pricing model
  const savedPricingModel = currentCampaignOrder?.pricingModel;
  const hideCpmFields = useMemo(() => {
    if (savedPricingModel) {
      return savedPricingModel === 'Percentage';
    }
    return isPercentageModel(selectedAdvertiser);
  }, [savedPricingModel, selectedAdvertiser]);

  const handleStartDateChange = (value) => {
    let updatedEndDate = endDate;
    // Normalize start date to start of day in user timezone, then convert to UTC for storage
    let updatedStartDate = value ? datePickerToStartOfDayUtc(value, timezone) : value;

    if (value) {
      if (value.isAfter(endDate)) {
        updatedEndDate = null;
      }
    }

    // Update start date using helper
    helpers.updateField('startDate', updatedStartDate);

    // Check if end date is set and is greater than 2 days after start date
    if (endDate && moment(endDate).isAfter(moment(value.format('YYYY-MM-DD')).add(2, 'days'))) {
      const numberOfDays = updatedEndDate ? updatedEndDate.diff(updatedStartDate, 'days') + 1 : 3;

      // Get current campaigns to update their dates
      const currentCampaigns = helpers.getFieldValue('campaigns') || [];
      const updatedCampaigns = currentCampaigns.map((campaign, index) => {
        if (index === 0) {
          return {
            ...campaign,
            startDate: updatedStartDate,
            endDate: updatedEndDate,
          };
        }
        return campaign;
      });

      helpers.setFieldsValue({
        startDate: updatedStartDate,
        endDate: updatedEndDate,
        numberOfDays,
        campaigns: updatedCampaigns,
      });
      return;
    }

    // Set end date 2 days after start date, normalized to end of day in user timezone, then UTC
    if (value) {
      updatedEndDate = moment.tz(value.format('YYYY-MM-DD'), timezone).add(2, 'days').endOf('day').utc();
      const numberOfDays = updatedEndDate.diff(updatedStartDate, 'days') + 1;

      // Get current campaigns to update their dates
      const currentCampaigns = helpers.getFieldValue('campaigns') || [];
      const updatedCampaigns = currentCampaigns.map((campaign, index) => {
        if (index === 0) {
          return {
            ...campaign,
            startDate: updatedStartDate,
            endDate: updatedEndDate,
          };
        }
        return campaign;
      });

      helpers.setFieldsValue({
        startDate: updatedStartDate,
        endDate: updatedEndDate,
        numberOfDays,
        campaigns: updatedCampaigns,
      });
    }
  };

  const handleEndDateChange = (value) => {
    // Normalize end date to end of day in user timezone, then convert to UTC for storage
    let updatedEndDate = value ? datePickerToEndOfDayUtc(value, timezone) : value;
    let updatedStartDate = startDate;

    if (value) {
      if (value.isBefore(startDate)) {
        updatedStartDate = null;
      }
    }

    // Update end date using helper
    helpers.updateField('endDate', updatedEndDate);

    // Check if start date is set and end date is greater than 2 days after start date
    if (startDate && moment(value.format('YYYY-MM-DD')).isAfter(moment(startDate.format('YYYY-MM-DD')).add(2, 'days'))) {
      const numberOfDays = updatedEndDate ? updatedEndDate.diff(updatedStartDate, 'days') + 1 : 3;

      // Get current campaigns to update their dates
      const currentCampaigns = helpers.getFieldValue('campaigns') || [];
      const updatedCampaigns = currentCampaigns.map((campaign, index) => {
        if (index === 0) {
          return {
            ...campaign,
            startDate: updatedStartDate,
            endDate: updatedEndDate,
          };
        }
        return campaign;
      });

      helpers.setFieldsValue({
        startDate: updatedStartDate,
        endDate: updatedEndDate,
        numberOfDays,
        // Cascade: clamp line items and flight schedules that exceed the new order end date
        campaigns: clampCampaignsToEndDate(updatedCampaigns, updatedEndDate),
      });
      return;
    }

    // Set start date 2 days before end date, normalized to start of day in user timezone, then UTC
    if (value) {
      updatedStartDate = moment.tz(value.format('YYYY-MM-DD'), timezone).subtract(2, 'days').startOf('day').utc();
      const numberOfDays = updatedEndDate.diff(updatedStartDate, 'days') + 1;

      // Get current campaigns to update their dates
      const currentCampaigns = helpers.getFieldValue('campaigns') || [];
      const updatedCampaigns = currentCampaigns.map((campaign, index) => {
        if (index === 0) {
          return {
            ...campaign,
            startDate: updatedStartDate,
            endDate: updatedEndDate,
          };
        }
        return campaign;
      });

      helpers.setFieldsValue({
        startDate: updatedStartDate,
        endDate: updatedEndDate,
        numberOfDays,
        // Cascade: clamp line items and flight schedules that exceed the new order end date
        campaigns: clampCampaignsToEndDate(updatedCampaigns, updatedEndDate),
      });
    }
  };

  const disabledEndDate = (date) => {
    if (!startDate) {
      return true;
    }

    const startInTz = toDatePickerDisplay(startDate, timezone);
    if (date.isBefore(startInTz)) {
      return true;
    }

    const start = startInTz.clone().startOf('day');
    const end = startInTz.clone().add(2, 'days').endOf('day');

    return date.isBetween(start, end, null, '[]');
  };

  const handleCpmBlur = (e) => {
    const value = Number(e.target.value);
    // Use the helper's CPM correction logic
    helpers.handleCpmBlur(value);
  };

  const handleCpmChange = () => {
    // Let Form.Item handle the value update automatically
  };

  // Handle budget input change (captures the value but doesn't update form immediately)
  const handleBudgetChange = (value) => {
    setBudgetInputValue(value);
  };

  // Debounced budget update - only updates form value after user stops typing for 500ms
  useEffect(() => {
    if (budgetInputValue === null) {
      return;
    }

    // Debounce the update by 500ms
    const timeoutId = setTimeout(() => {
      helpers.updateField('budget', budgetInputValue);
    }, 500);

    // Cleanup: cancel the timeout if budgetInputValue changes again before 500ms
    return () => clearTimeout(timeoutId);
  }, [budgetInputValue, helpers]);

  useEffect(() => {
    const checkPermission = async () => {
      const authorized = await isAuthorized(['isTrafficker', 'isProcessor'], []);
      setCanEdit(authorized);
    };

    checkPermission();
  }, [isAuthorized]);

  // ─── Renew ──────────────────────────────────────────────────────────
  // Appends monthly renewal flights at every level (order budget + end date,
  // campaign flights, line item flights pro-rata). See utils/renewalFlights.
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [renewMonths, setRenewMonths] = useState(1);
  const [renewMonthlyBudget, setRenewMonthlyBudget] = useState(null);
  const [renewEffectiveDate, setRenewEffectiveDate] = useState(null);

  const renewalOptions = {
    newMonthlyBudget: renewMonthlyBudget,
    effectiveDate: renewEffectiveDate,
  };

  const renewalPreview = showRenewModal
    ? applyRenewalToCampaigns(helpers.getFieldValue('campaigns') || [], renewMonths, timezone, renewalOptions)
    : null;

  const handleRenewConfirm = () => {
    const campaigns = helpers.getFieldValue('campaigns') || [];
    const result = applyRenewalToCampaigns(campaigns, renewMonths, timezone, renewalOptions);

    if (!result) {
      message.info('No existing flight schedules to renew from');
      setShowRenewModal(false);
      return;
    }

    const updates = {
      campaigns: result.campaigns,
      budget: Number(((Number(budget) || 0) + result.totalRenewal).toFixed(2)),
    };

    // Renewal only ever extends the order end date
    if (
      result.furthestEndDate &&
      (!endDate || moment(result.furthestEndDate).isAfter(moment(endDate)))
    ) {
      updates.endDate = result.furthestEndDate;
      if (startDate) {
        updates.numberOfDays = moment(result.furthestEndDate).diff(moment(startDate), 'days') + 1;
      }
    }

    helpers.setFieldsValue(updates);
    setShowRenewModal(false);
    setRenewMonths(1);
    setRenewMonthlyBudget(null);
    setRenewEffectiveDate(null);
    message.success(
      `Renewed ${result.renewedCount} campaign(s) for ${renewMonths} month(s) — added $${result.totalRenewal.toFixed(2)}`
    );
  };

  return (
    <>
      <Card id="requester-duration-budget" title="ORDER BUDGET & SCHEDULE">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {(() => {
            // "Today" must use the user's LOCAL browser calendar day — NOT moment.tz(timezone).
            // When the org timezone is UTC, moment.tz('UTC') can be a calendar day AHEAD of
            // the user's local day (e.g. still 06/15 locally but already 06/16 in UTC), which
            // wrongly flags today's start date as "in the past". This mirrors the Launch
            // Readiness check (validateAllDatesForLaunch) so the two stay consistent.
            const todayStr = moment().format('YYYY-MM-DD');
            const campaignStartStr = startDate
              ? (moment.isMoment(startDate)
                  ? startDate.utc().format('YYYY-MM-DD')
                  : moment.utc(startDate).format('YYYY-MM-DD'))
              : null;
            const campaignStatus = currentCampaignOrder?.status;

            if (startDate && campaignStartStr < todayStr) {
              // Hide notice if campaign is already live or live_pending
              if (
                campaignStatus === 'LIVE' ||
                campaignStatus === 'LIVE_PENDING' ||
                campaignStatus === 'LIVE_APPROVED'
              ) {
                return null;
              }

              return (
                <Alert
                  message="Start Date Notice"
                  description="The campaign start date is in the past."
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              );
            }
            return null;
          })()}

          <Row gutter={20}>
            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Campaign Budget</Text>
                <Text type="secondary">Campaign total spend</Text>
                <Form.Item
                  name="budget"
                  rules={[
                    {
                      required: true,
                      message: 'Please input a budget',
                    },
                    () => ({
                      validator(_, value) {
                        if (value >= 1 || value === '') {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Please input a valid budget'));
                      },
                    }),
                  ]}
                >
                  <InputNumber
                    addonBefore="$USD"
                    style={{ width: '100%' }}
                    min={0}
                    max={Math.pow(10, 12) - 1}
                    maxLength={12}
                    onChange={handleBudgetChange}
                  />
                </Form.Item>
              </Space>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Start Date</Text>
                <Text type="secondary">Leave blank for ASAP</Text>
                <Form.Item
                  name="startDate"
                  rules={[
                    {
                      required: true,
                      message: 'Please input a start date',
                    },
                  ]}
                >
                  <TimezoneDatePicker
                    timezone={timezone}
                    onChange={handleStartDateChange}
                    // Compare calendar days as STRINGS, not instants: the
                    // picker's cells are UTC-midnight moments (the value is
                    // tz-shifted by toDatePickerDisplay, and rc-picker clones
                    // preserve that offset), so an instant comparison against
                    // moment().startOf('day') (browser-local midnight) disables
                    // today all day for any browser behind UTC. Judging by the
                    // cell's displayed day vs the user's local day keeps the
                    // comparison in one frame regardless of timezone.
                    disabledDate={(date) =>
                      !canEdit ? date.format('YYYY-MM-DD') < moment().format('YYYY-MM-DD') : false
                    }
                  />
                </Form.Item>
              </Space>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>End Date</Text>
                <Text type="secondary">Date campaign will end</Text>
                <Form.Item
                  name="endDate"
                  rules={[
                    {
                      required: true,
                      message: 'Please input an end date',
                    },
                  ]}
                >
                  <TimezoneDatePicker
                    timezone={timezone}
                    onChange={handleEndDateChange}
                    disabledDate={disabledEndDate}
                  />
                </Form.Item>
              </Space>
            </Col>
          </Row>

          <Row>
            <Col>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  setRenewMonths(1);
                  setRenewMonthlyBudget(null);
                  setRenewEffectiveDate(null);
                  setShowRenewModal(true);
                }}
              >
                Renew
              </Button>
            </Col>
          </Row>
        </Space>
      </Card>

      <Modal
        title="Renew Campaign Order"
        visible={showRenewModal}
        onOk={handleRenewConfirm}
        onCancel={() => setShowRenewModal(false)}
        okText="Renew"
        okButtonProps={{ disabled: !renewalPreview }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text>Number of months to renew:</Text>
            <InputNumber
              style={{ marginLeft: 8 }}
              min={1}
              max={24}
              value={renewMonths}
              onChange={(value) => setRenewMonths(value || 1)}
            />
          </div>
          <div>
            <Text>New monthly budget (optional):</Text>
            <InputNumber
              style={{ marginLeft: 8 }}
              addonBefore="$"
              min={0.01}
              precision={2}
              placeholder="Same as current rate"
              value={renewMonthlyBudget}
              onChange={(value) => setRenewMonthlyBudget(value)}
            />
          </div>
          <div>
            <Text>Rate change effective date (optional):</Text>
            <DatePicker
              style={{ marginLeft: 8 }}
              value={renewEffectiveDate}
              onChange={(date) => setRenewEffectiveDate(date)}
              placeholder="First renewal day"
            />
          </div>
          {renewalPreview?.rateChange && (
            <Alert
              type="info"
              showIcon
              message={`Rate change: $${renewalPreview.rateChange.oldMonthlyBudget.toFixed(2)} → $${renewalPreview.rateChange.newMonthlyBudget.toFixed(2)} per month`}
              description={
                renewalPreview.rateChange.split
                  ? 'The current flight will be split at the effective date with daily proration (old rate before, new rate after).'
                  : 'The new rate takes effect at the next flight boundary.'
              }
            />
          )}
          {renewalPreview ? (
            <>
              <Text type="secondary">
                Adds {renewMonths} monthly flight(s) to {renewalPreview.renewedCount} campaign(s),
                mirroring the last flight's budget and dates. Delivery line items extend at their
                own monthly rate; reference (customer-facing) line items share the renewal pro-rata.
              </Text>
              <Text type="secondary">
                Total added budget: ${renewalPreview.totalRenewal.toFixed(2)} — new end date:{' '}
                {moment(renewalPreview.furthestEndDate).format('MM/DD/YYYY')}.
              </Text>
              {renewalPreview.skippedCount > 0 && (
                <Text type="warning">
                  {renewalPreview.skippedCount} campaign(s) have no flight schedule and will be
                  skipped.
                </Text>
              )}
            </>
          ) : (
            <Text type="warning">No existing flight schedules to renew from.</Text>
          )}
        </Space>
      </Modal>
    </>
  );
};

export default CampaignRequestorBudget;
